import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Textarea } from '@/components/ui/textarea';
import { CompanySearch, type CompanyOption } from '@/components/CompanySearch';
import { useOppPicklists, useResources, useMe, useCompanyContacts, useCreateOpportunity } from '@/api/hooks';
import { fmtCurrency } from '@/lib/cobalt-helpers';
import { ROUTES, oppPath } from '@/lib/routes';
import { useClientScope } from '@/contexts/ClientScopeContext';

// Stage 1 - Opportunity (Synfo default)
const DEFAULT_STAGE = '29682770';

const ENQUIRY_TYPES = ['New Business', 'Existing Business'] as const;
const PRIORITY_UDF = ['Low', 'Medium', 'High'] as const;

const SELECT_CLS = 'flex h-9 w-full rounded-md border border-input bg-background text-foreground px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

function FormRow({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[180px_1fr] sm:items-start sm:gap-4">
      <div className="pt-2">
        <span className="text-sm font-medium text-muted-foreground">
          {label}{required && <span className="ml-0.5 text-destructive">*</span>}
        </span>
        {hint && <p className="text-[11px] text-muted-foreground/70">{hint}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function SectionHead({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-t border-border pt-4">
      <p className="mb-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</p>
    </div>
  );
}

export default function CreateOpportunityPage() {
  const navigate = useNavigate();
  const picklists = useOppPicklists();
  const resources = useResources();
  const me = useMe();
  const createOpportunity = useCreateOpportunity();

  // Core
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // Pre-filled from the client picked in the header (still changeable).
  const { scope } = useClientScope();
  const [company, setCompany] = useState<CompanyOption | null>(scope ? { id: scope.companyId, name: scope.name } : null);
  const [contactID, setContactID] = useState('');
  const [stage, setStage] = useState(DEFAULT_STAGE);

  // Classification
  const [leadSource, setLeadSource] = useState('');
  const [rating, setRating] = useState('');
  const [enquiryType, setEnquiryType] = useState('');
  const [serviceCategory, setServiceCategory] = useState('');
  const [priorityUdf, setPriorityUdf] = useState('Low');

  // Revenue
  const [professionalServices, setProfessionalServices] = useState('');
  const [hardwareFees, setHardwareFees] = useState('');
  const [mrr, setMrr] = useState('');
  const [cost, setCost] = useState('');

  // Pipeline
  const [probability, setProbability] = useState('20');
  const [projectedCloseDate, setProjectedCloseDate] = useState('');

  // Ownership
  const [ownerResourceID, setOwnerResourceID] = useState('');
  const [nextStep, setNextStep] = useState('');

  const contacts = useCompanyContacts(company?.id ?? null);

  useEffect(() => { setContactID(''); }, [company?.id]);

  // Default owner to current user once loaded
  useEffect(() => {
    if (me.data?.item?.id && !ownerResourceID) {
      setOwnerResourceID(String(me.data.item.id));
    }
  }, [me.data, ownerResourceID]);

  const calculatedAmount = useMemo(() => {
    return (Number(professionalServices) || 0) +
           (Number(hardwareFees) || 0) +
           (Number(mrr) || 0);
  }, [professionalServices, hardwareFees, mrr]);

  const canSubmit = title.trim() && company && stage;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const udfs: { name: string; value: string }[] = [];
    if (enquiryType) udfs.push({ name: 'Enquiry Type', value: enquiryType });
    if (serviceCategory) udfs.push({ name: 'Service Category', value: serviceCategory });
    if (priorityUdf) udfs.push({ name: 'Priority', value: priorityUdf });

    const fields: Record<string, unknown> = {
      title: title.trim(),
      companyID: company!.id,
      stage: Number(stage),
      probability: Number(probability) || 20,
      advancedField1: Number(professionalServices) || 0,
      advancedField3: Number(hardwareFees) || 0,
      monthlyRevenue: Number(mrr) || 0,
      cost: Number(cost) || 0,
    };
    if (contactID) fields.contactID = Number(contactID);
    if (leadSource) fields.leadSource = Number(leadSource);
    if (rating) fields.rating = Number(rating);
    if (ownerResourceID) fields.ownerResourceID = Number(ownerResourceID);
    if (projectedCloseDate) fields.projectedCloseDate = new Date(projectedCloseDate).toISOString();
    if (description.trim()) fields.description = description.trim();
    if (nextStep.trim()) fields.nextStep = nextStep.trim();
    if (udfs.length) fields.userDefinedFields = udfs;

    createOpportunity.mutate(fields, {
      onSuccess: (data) => {
        const id = (data as any)?.itemId ?? (data as any)?.item?.id ?? (data as any)?.id;
        toast.success('Opportunity created');
        navigate(id ? oppPath(id) : ROUTES.psa.opportunities.all);
      },
      onError: (err) => toast.error('Failed to create opportunity', {
        description: err instanceof Error ? err.message : 'Unknown error',
      }),
    });
  };

  const pl = picklists.data;

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-4">
      <div className="mb-6 flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-[22px] font-[650] tracking-[-0.018em]">New Opportunity</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm">

        {/* Core */}
        <FormRow label="Title" required>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Opportunity name" required />
        </FormRow>

        <FormRow label="Description">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What the client needs, scope, work involved…" rows={4} />
        </FormRow>

        <FormRow label="Company" required>
          <CompanySearch value={company} onChange={setCompany} allowCreate />
        </FormRow>

        {company && (
          <FormRow label="Contact">
            <select value={contactID} onChange={(e) => setContactID(e.target.value)} className={SELECT_CLS} disabled={contacts.isLoading}>
              <option value="">{contacts.isLoading ? 'Loading…' : '— none —'}</option>
              {(contacts.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.firstName} {c.lastName}{c.emailAddress ? ` — ${c.emailAddress}` : ''}
                </option>
              ))}
            </select>
          </FormRow>
        )}

        <FormRow label="Stage" required>
          <select value={stage} onChange={(e) => setStage(e.target.value)} required className={SELECT_CLS}>
            <option value="">Select stage…</option>
            {(pl?.stage ?? []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FormRow>

        {/* Classification */}
        <SectionHead>Classification</SectionHead>
        <div className="space-y-4">
          <FormRow label="Lead Source">
            <select value={leadSource} onChange={(e) => setLeadSource(e.target.value)} className={SELECT_CLS}>
              <option value="">— none —</option>
              {(pl?.leadSource ?? []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </FormRow>

          <FormRow label="Enquiry Type">
            <select value={enquiryType} onChange={(e) => setEnquiryType(e.target.value)} className={SELECT_CLS}>
              <option value="">— none —</option>
              {ENQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </FormRow>

          <FormRow label="Service Category">
            <select value={serviceCategory} onChange={(e) => setServiceCategory(e.target.value)} className={SELECT_CLS}>
              <option value="">— none —</option>
              {(pl?.serviceCategory ?? []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </FormRow>

          <FormRow label="Priority">
            <select value={priorityUdf} onChange={(e) => setPriorityUdf(e.target.value)} className={SELECT_CLS}>
              {PRIORITY_UDF.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </FormRow>
        </div>

        {/* Revenue */}
        <SectionHead>Revenue (AUD)</SectionHead>
        <div className="space-y-4">
          <FormRow label="Professional Services">
            <Input type="number" min="0" step="0.01" value={professionalServices} onChange={(e) => setProfessionalServices(e.target.value)} placeholder="0.00" />
          </FormRow>

          <FormRow label="Hardware Fees">
            <Input type="number" min="0" step="0.01" value={hardwareFees} onChange={(e) => setHardwareFees(e.target.value)} placeholder="0.00" />
          </FormRow>

          <FormRow label="Monthly Recurring">
            <Input type="number" min="0" step="0.01" value={mrr} onChange={(e) => setMrr(e.target.value)} placeholder="0.00" />
          </FormRow>

          <FormRow label="Cost">
            <Input type="number" min="0" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" />
          </FormRow>

          <FormRow label="Calculated Amount" hint="Services + Hardware + MRR">
            <div className="flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm font-medium tabular-nums text-muted-foreground">
              {fmtCurrency(calculatedAmount)}
            </div>
          </FormRow>
        </div>

        {/* Pipeline */}
        <SectionHead>Pipeline</SectionHead>
        <div className="space-y-4">
          <FormRow label="Probability %">
            <Input type="number" min="0" max="100" step="1" value={probability} onChange={(e) => setProbability(e.target.value)} placeholder="20" />
          </FormRow>

          <FormRow label="Projected Close">
            <DatePicker size="md" className="w-full" value={projectedCloseDate || null} onChange={(v) => setProjectedCloseDate(v ?? '')} placeholder="Any date" />
          </FormRow>

          <FormRow label="Rating">
            <select value={rating} onChange={(e) => setRating(e.target.value)} className={SELECT_CLS}>
              <option value="">— none —</option>
              {(pl?.rating ?? []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </FormRow>
        </div>

        {/* Ownership */}
        <SectionHead>Ownership</SectionHead>
        <div className="space-y-4">
          <FormRow label="Owner">
            <select value={ownerResourceID} onChange={(e) => setOwnerResourceID(e.target.value)} className={SELECT_CLS}>
              <option value="">— none —</option>
              {(resources.data?.items ?? []).map((r) => (
                <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>
              ))}
            </select>
          </FormRow>

          <FormRow label="Next Step">
            <Input value={nextStep} onChange={(e) => setNextStep(e.target.value)} placeholder="e.g. Book discovery call" />
          </FormRow>
        </div>

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={!canSubmit || createOpportunity.isPending}>
            {createOpportunity.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Opportunity
          </Button>
        </div>
      </form>
    </div>
  );
}
